import React, { useState, useMemo } from "react";
import { ButtonTertiaryCompact, TooltipV2 } from "oolib";
import { ButtonGhost } from "oolib/dist/v2/components/Buttons";
import { useShowFilterModuleV3 } from "@/components/discovery/FilterModule/hooks/useShowFilterModuleV3";

/**
 * Encapsulates lightbox anchor state, open/close state, clear logic,
 * and the header-right JSX shared by every widget ConfigForm's filter section.
 *
 * @param {Object} [options]
 * @param {boolean} [options.forceEnabled] - bypass the v3 feature flag (e.g. for Sankey)
 * @param {boolean} [options.hasDimensions] - whether any dimension / data field is configured on the widget
 *
 * Returns:
 *  - filterHeaderProps:   spread onto <ConfigForm_SectionWrapper> ({ headerRight })
 *  - filterControlProps:  spread onto <WidgetFilters> or <FilterModule>
 *  - filterActionButtons: raw button JSX for custom headers (e.g. FilterSidebar)
 *  - showFilterModuleV3:  the feature flag value, in case callers need it
 */
export const useWidgetFilterControls = ({ forceEnabled, hasDimensions = true } = {}) => {
  // Reports render annotation filters, so they only get V3 when the annotation
  // hierarchy exists (see useShowFilterModuleV3).
  const { showFilterModuleV3 } = useShowFilterModuleV3({ usesAnnoFilters: true });
  const enabled = forceEnabled ?? showFilterModuleV3;

  const [addFilterAnchorEl, setAddFilterAnchorEl] = useState(null);
  const [addFilterOpen, setAddFilterOpen] = useState(false);
  const [clearFilters, setClearFilters] = useState(null);
  const [hasFiltersToClear, setHasFiltersToClear] = useState(false);
  const [hasFilterConfigs, setHasFilterConfigs] = useState(null);

  const plusButtonState = useMemo(() => {
    if (!hasDimensions) {
      return { disabled: true, tooltip: "Select dimensions to enable filters" };
    }
    if (hasFilterConfigs === false) {
      return { disabled: true, tooltip: "No filter combination present" };
    }
    return { disabled: false, tooltip: null };
  }, [hasDimensions, hasFilterConfigs]);

  const filterActionButtons = enabled ? (
    <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
      {hasFiltersToClear && (
        <ButtonGhost icon="X" style={{ padding: "4px" }} onClick={() => clearFilters?.()} />
      )}
      <div ref={!plusButtonState.disabled ? setAddFilterAnchorEl : undefined} style={{ display: "inline-flex" }}>
        {plusButtonState.disabled ? (
          <TooltipV2 text={plusButtonState.tooltip} placement="top">
            <span style={{ display: "inline-block" }}>
              <ButtonGhost icon="Plus" style={{ padding: "4px" }} disabled />
            </span>
          </TooltipV2>
        ) : (
          <ButtonGhost
            icon="Plus"
            style={{ padding: "4px" }}
            onClick={() => setAddFilterOpen((prev) => !prev)}
          />
        )}
      </div>
    </div>
  ) : null;

  const sidebarFilterActionButtons = enabled ? (
    <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
      {hasFiltersToClear && (
        <ButtonTertiaryCompact responsive={true} value="Clear" icon="X" onClick={() => clearFilters?.()} />
      )}
      <div ref={!plusButtonState.disabled ? setAddFilterAnchorEl : undefined} style={{ display: "inline-flex" }}>
        {plusButtonState.disabled ? (
          <TooltipV2 text={plusButtonState.tooltip} placement="top">
            <span style={{ display: "inline-block" }}>
              <ButtonTertiaryCompact responsive={true} value="Add Filter" icon="Plus" disabled />
            </span>
          </TooltipV2>
        ) : (
          <ButtonTertiaryCompact
            responsive={true}
            value="Add Filter"
            icon="Plus"
            onClick={() => setAddFilterOpen((prev) => !prev)}
          />
        )}
      </div>
    </div>
  ) : null;

  const filterHeaderProps = filterActionButtons
    ? { headerRight: filterActionButtons }
    : {};

  const filterControlProps = enabled
    ? {
        addFilterAnchorEl,
        addFilterLightboxOpen: addFilterOpen,
        onAddFilterLightboxOpenChange: setAddFilterOpen,
        onClearFilters: (fn) => setClearFilters(() => fn),
        onHasFiltersToClear: setHasFiltersToClear,
        onHasFilterConfigs: setHasFilterConfigs,
        hasDimensions,
      }
    : {};

  return { filterHeaderProps, filterControlProps, filterActionButtons, sidebarFilterActionButtons, showFilterModuleV3 };
};
